import React, { useMemo } from 'react';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useTranslation } from 'react-i18next';
import {
  DATETIME_FORMAT_DATETIME,
  formattedMorphUserName,
} from '../../../../../utils/helpers/CommonHelper.jsx';
import Tooltip from '../../../../common/Tooltip/Tooltip.jsx';
import dayjs from 'dayjs';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { UserDeleteOutlined } from '@ant-design/icons';

const getEmailFromParticipantable = (participantable) => {
  if (!participantable) return '';
  return participantable.user_type === 'ExternalUser'
    ? participantable.email || ''
    : participantable.mail || '';
};

const getPhoneFromParticipantable = (participantable) => {
  if (!participantable) return '';
  return participantable.phone_number || '-';
};

const EduParticipantsTable = ({ participants, onUnenroll }) => {
  const { t } = useTranslation();

  const nameFilterProps = useDataGridTextFilter(
    'participantable',
    formattedMorphUserName,
  );
  const emailFilterProps = useDataGridTextFilter(
    'participantable',
    getEmailFromParticipantable,
  );
  const phoneFilterProps = useDataGridTextFilter(
    'participantable',
    getPhoneFromParticipantable,
  );
  const labelDateFilterProps = useDataGridDateRangeFilter(['created_at']);

  const columns = useMemo(
    () => [
      {
        title: t('Meno'),
        dataIndex: ['participantable'],
        render: (participantable) => formattedMorphUserName(participantable),
        sorter: dataGridTextSorter('participantable', formattedMorphUserName),
        ...nameFilterProps,
      },
      {
        title: t('E-mailová adresa'),
        dataIndex: ['participantable'],
        render: (participantable) =>
          getEmailFromParticipantable(participantable),
        sorter: dataGridTextSorter(
          'participantable',
          getEmailFromParticipantable,
        ),
        ...emailFilterProps,
      },
      {
        title: t('Telefón'),
        dataIndex: ['participantable'],
        render: (participantable) =>
          getPhoneFromParticipantable(participantable),
        sorter: dataGridTextSorter(
          'participantable',
          getPhoneFromParticipantable,
        ),
        ...phoneFilterProps,
      },
      {
        title: t('Dátum prihlásenia'),
        dataIndex: ['created_at'],
        sorter: dataGridTextSorter(['created_at']),
        render: (created_at) => {
          const value = created_at
            ? dayjs(new Date(created_at)).format(DATETIME_FORMAT_DATETIME)
            : '';
          return (
            <Tooltip tooltip={value}>
              <div style={{ width: '100%' }}>{value}</div>
            </Tooltip>
          );
        },
        ...labelDateFilterProps,
      },
      {
        title: t('Akcie'),
        key: 'actions',
        width: 120,
        render: (_, row) => (
          <div className="d-flex">
            <IconButton
              icon={<UserDeleteOutlined />}
              tooltip={t('Odhlásiť účastníka')}
              onClick={(e) => {
                e.stopPropagation();
                onUnenroll?.(row);
              }}
            />
          </div>
        ),
      },
    ],
    [
      t,
      nameFilterProps,
      emailFilterProps,
      phoneFilterProps,
      labelDateFilterProps,
      onUnenroll,
    ],
  );

  return (
    <DataGrid
      rowKey={(row) => row.id}
      columns={columns}
      dataSource={Array.isArray(participants) ? participants : []}
      permissionsBasic={'fri-edu-course-dates'}
      uniqueTableId={'edu-course-date-participants'}
      disableSettings
    />
  );
};

export default EduParticipantsTable;
